import { useEffect, useRef } from "react";

import {
  FAVORITE_WELL_LIST_EVENTS,
  getFavoriteWellListId,
  removeFavoriteWellListId,
  setFavoriteWellListId
} from "utils/typeWellFavoriteStorage";

import { WellListRecordCollection } from "models/UserArpsModel";

interface UseFavoriteWellListSyncProps {
  forecastId: string | undefined;
  data: WellListRecordCollection;
  isOptimisticallyUpdating: boolean;
  hasUserInteracted: boolean;
  setFavoriteChangeId: (id: string | null) => void;
  setHasUserInteracted: (value: boolean) => void;
}

/**
 * Custom hook to manage favorite well list synchronization
 *
 * Strategy: Sync from API on page load/reload, then use localStorage
 * - On page load/reload: Sync from API's isFavorite flag to localStorage
 * - On forecast change: Load from localStorage
 * - After user interaction: Optimistic update in handleToggleFavorite handles localStorage
 */
export function useFavoriteWellListSync({
  forecastId,
  data,
  isOptimisticallyUpdating,
  hasUserInteracted,
  setFavoriteChangeId,
  setHasUserInteracted
}: UseFavoriteWellListSyncProps) {
  // Track if we've synced from API for each forecast
  const hasSyncedFromAPI = useRef<Set<string>>(new Set());

  // Load favorite from localStorage when forecast changes
  useEffect(() => {
    if (!forecastId) {
      setFavoriteChangeId(null);
      return;
    }

    // Get favorite from localStorage
    const savedFavoriteId = getFavoriteWellListId(forecastId);
    setFavoriteChangeId(savedFavoriteId);

    // Reset interaction flag to allow API sync
    setHasUserInteracted(false);
  }, [forecastId, setFavoriteChangeId, setHasUserInteracted]);

  // Listen for storage changes from other tabs/windows/widgets
  useEffect(() => {
    if (!forecastId) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      // Check if the change is for our forecast's favorite well list
      const favoriteStorageKey = `typewell-favorite-welllist-${forecastId}`;
      if (e.key === favoriteStorageKey) {
        // Another component/tab updated the favorite - sync the state
        const newValue = e.newValue;
        setFavoriteChangeId(newValue);
      }
    };

    const handleCustomEvent = (e: CustomEvent) => {
      // Check if the change is for our forecast
      if (e.detail?.forecastId === forecastId) {
        // Another widget on the same page updated the favorite
        setFavoriteChangeId(e.detail.changeId);
      }
    };

    // Listen for storage events (cross-tab)
    window.addEventListener(FAVORITE_WELL_LIST_EVENTS.STORAGE, handleStorageChange);

    // Listen for custom events (same-page widgets)
    window.addEventListener(
      FAVORITE_WELL_LIST_EVENTS.FAVORITE_CHANGED,
      handleCustomEvent as EventListener
    );

    return () => {
      window.removeEventListener(FAVORITE_WELL_LIST_EVENTS.STORAGE, handleStorageChange);
      window.removeEventListener(
        FAVORITE_WELL_LIST_EVENTS.FAVORITE_CHANGED,
        handleCustomEvent as EventListener
      );
    };
  }, [forecastId, setFavoriteChangeId]);

  // Sync from API on initial load or page reload
  useEffect(() => {
    // Skip if optimistic update in progress
    if (isOptimisticallyUpdating) {
      return;
    }

    // Skip if user has interacted (trust optimistic update)
    if (hasUserInteracted) {
      return;
    }

    if (!forecastId || !data?.wellListRecords) {
      return;
    }

    // Skip if we've already synced from API for this forecast in this session
    if (hasSyncedFromAPI.current.has(forecastId)) {
      return;
    }

    // Check if data belongs to current forecast (prevent stale data sync)
    const dataForecastId = data.wellListRecords[0]?.forecastId;
    if (dataForecastId && dataForecastId !== forecastId) {
      return;
    }

    // Find favorite from API response
    const favoriteRecord = data.wellListRecords.find((record) => record.isFavorite);
    const favoriteId = favoriteRecord?.changeId || null;

    // Sync from API to localStorage
    if (favoriteId) {
      setFavoriteWellListId(forecastId, favoriteId);
    } else {
      removeFavoriteWellListId(forecastId);
    }

    // Update state
    setFavoriteChangeId(favoriteId);

    // Mark as synced for this session
    hasSyncedFromAPI.current.add(forecastId);
  }, [
    forecastId,
    data?.wellListRecords,
    isOptimisticallyUpdating,
    hasUserInteracted,
    setFavoriteChangeId
  ]);
}
